import { useState, useEffect, useRef, useCallback } from "react";
import { ShoppingCart, Eye, Heart, Star, Zap, Shield, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCartStore } from "../stores/useCartStore";
import { useNavigate } from "react-router-dom";
import { getProductImages } from "../lib/imageUtils";

const ProductCard = ({ product }) => {
	const [currentImageIndex, setCurrentImageIndex] = useState(0);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const [isWishlisted, setIsWishlisted] = useState(false);
	const [imageLoaded, setImageLoaded] = useState(false);
	const modalRef = useRef(null);
	const { addToCart } = useCartStore();
	const navigate = useNavigate();

	// יצירת מערך תמונות באמצעות הפונקציה החדשה
	const images = getProductImages(product);

	const handleImageChange = useCallback((direction) => {
		if (direction === 'next') {
			setCurrentImageIndex((prev) => (prev + 1) % images.length);
		} else {
			setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
		}
	}, [images.length]);

	const handleViewDetails = useCallback(() => {
		console.log('🔍 ProductCard: handleViewDetails clicked');
		console.log('🆔 Product ID:', product?._id);
		console.log('📦 Full product:', product);
		
		if (!product || !product._id) {
			console.error('❌ ProductCard: No product ID available');
			return;
		}
		
		console.log('🚀 ProductCard: Navigating to /product/' + product._id);
		navigate(`/product/${product._id}`);
	}, [product, navigate]);

	const handleImageClick = useCallback(() => {
		console.log('Image clicked, opening modal');
		setIsModalOpen(true);
	}, []);

	const handleModalClose = useCallback(() => {
		setIsModalOpen(false);
	}, []);

	const handleWishlistToggle = useCallback((e) => {
		e.stopPropagation();
		setIsWishlisted(!isWishlisted);
	}, [isWishlisted]);

	const handleAddToCart = useCallback((e) => {
		e.stopPropagation();
		addToCart(product);
	}, [addToCart, product]);

	// Auto-advance images when hovered
	useEffect(() => {
		let interval;
		if (isHovered && images.length > 1) {
			interval = setInterval(() => {
				setCurrentImageIndex((prev) => (prev + 1) % images.length);
			}, 2000);
		}
		return () => {
			if (interval) clearInterval(interval);
		};
	}, [isHovered, images.length]);

	// Handle image load
	const handleImageLoad = useCallback(() => {
		setImageLoaded(true);
		console.log('✅ Image loaded successfully:', images[currentImageIndex], 'for product:', product?.name);
	}, [currentImageIndex, images, product?.name]);

	// Handle image error
	const handleImageError = useCallback((e) => {
		console.log('❌ Image failed to load:', e.target.src);
		e.target.src = '/images/placeholder.svg';
		setImageLoaded(true);
	}, []);

	// Click outside modal to close
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (modalRef.current && !modalRef.current.contains(event.target)) {
				handleModalClose();
			}
		};

		if (isModalOpen) {
			document.addEventListener('mousedown', handleClickOutside);
			document.body.style.overflow = 'hidden';
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.body.style.overflow = 'unset';
		};
	}, [isModalOpen, handleModalClose]);

	if (!product) {
		return null;
	}

	const price = product.price || 0;
	const originalPrice = product.originalPrice || price;
	const hasDiscount = originalPrice > price;
	const discountPercentage = hasDiscount ? Math.round(((originalPrice - price) / originalPrice) * 100) : 0;

	return (
		<>
			<motion.div
				className="group relative bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100"
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				whileHover={{ 
					y: -8,
					transition: { duration: 0.3, ease: "easeOut" }
				}}
				layout
			>
				{/* Gradient Overlay על הכרטיס */}
				<div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-gray-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10 pointer-events-none" />
				
				{/* תוויות מעמד */}
				<div className="absolute top-4 left-4 z-20 flex flex-col gap-2">
					{product.isNewProduct && (
						<motion.span 
							className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-xs font-bold rounded-full shadow-lg"
							initial={{ scale: 0, rotate: -10 }}
							animate={{ scale: 1, rotate: 0 }}
							transition={{ delay: 0.2 }}
						>
							<Sparkles className="w-3 h-3" />
							חדש
						</motion.span>
					)}
					{hasDiscount && (
						<motion.span 
							className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-red-500 to-pink-600 text-white text-xs font-bold rounded-full shadow-lg"
							initial={{ scale: 0, rotate: 10 }}
							animate={{ scale: 1, rotate: 0 }}
							transition={{ delay: 0.3 }}
						>
							<Zap className="w-3 h-3" />
							{discountPercentage}% הנחה
						</motion.span>
					)}
					{product.isBestSeller && (
						<motion.span 
							className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-xs font-bold rounded-full shadow-lg"
							initial={{ scale: 0, rotate: -5 }}
							animate={{ scale: 1, rotate: 0 }}
							transition={{ delay: 0.4 }}
						>
							<Star className="w-3 h-3" />
							רב מכר
						</motion.span>
					)}
				</div>

				{/* כפתור Wishlist */}
				<motion.button 
					onClick={handleWishlistToggle}
					className={`absolute top-4 right-4 z-20 p-3 rounded-full transition-all duration-300 backdrop-blur-md shadow-lg ${
						isWishlisted 
							? 'bg-red-500/90 text-white shadow-red-500/25' 
							: 'bg-white/90 text-gray-600 hover:text-red-500 hover:bg-red-50'
					}`}
					whileHover={{ scale: 1.1 }}
					whileTap={{ scale: 0.95 }}
				>
					<Heart className={`w-5 h-5 ${isWishlisted ? 'fill-current' : ''}`} />
				</motion.button>

				{/* אזור התמונה */}
				<div className="relative aspect-square overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
					{!imageLoaded && (
						<div className="absolute inset-0 flex items-center justify-center">
							<div className="w-8 h-8 border-4 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
						</div>
					)}
					
					<motion.img
						src={images[currentImageIndex]}
						alt={product.name || 'מוצר'}
						className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 cursor-pointer"
						onClick={handleImageClick}
						onLoad={handleImageLoad}
						onError={handleImageError}
						initial={{ opacity: 0 }}
						animate={{ opacity: imageLoaded ? 1 : 0 }}
						transition={{ duration: 0.3 }}
					/>

					{/* ניווט תמונות */}
					{images.length > 1 && isHovered && (
						<>
							<motion.button
								onClick={(e) => {
									e.stopPropagation();
									handleImageChange('prev');
								}}
								className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-all duration-200"
								initial={{ opacity: 0, x: -10 }}
								animate={{ opacity: 1, x: 0 }}
								whileHover={{ scale: 1.1 }}
								whileTap={{ scale: 0.95 }}
							>
								<ChevronLeft className="w-4 h-4 text-gray-700" />
							</motion.button>
							<motion.button
								onClick={(e) => {
									e.stopPropagation();
									handleImageChange('next');
								}}
								className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-all duration-200"
								initial={{ opacity: 0, x: 10 }}
								animate={{ opacity: 1, x: 0 }}
								whileHover={{ scale: 1.1 }}
								whileTap={{ scale: 0.95 }}
							>
								<ChevronRight className="w-4 h-4 text-gray-700" />
							</motion.button>
						</>
					)}

					{/* נקודות ניווט */}
					{images.length > 1 && (
						<div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
							{images.map((_, index) => (
								<button
									key={index}
									onClick={(e) => {
										e.stopPropagation();
										setCurrentImageIndex(index);
									}}
									className={`w-2 h-2 rounded-full transition-all duration-200 ${
										index === currentImageIndex 
											? 'bg-white shadow-lg scale-125' 
											: 'bg-white/60 hover:bg-white/80'
									}`}
								/>
							))}
						</div>
					)}

					{/* גרדיאנט תחתון */}
					<div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
				</div>

				{/* פרטי המוצר */}
				<div className="p-6 space-y-4">
					{/* שם המוצר */}
					<motion.h3 
						className="text-lg font-bold text-gray-900 cursor-pointer hover:text-purple-600 transition-colors duration-300 line-clamp-2 leading-tight"
						onClick={handleViewDetails}
						whileHover={{ x: 2 }}
					>
						{product.name || 'מוצר'}
					</motion.h3>

					{/* קטגוריה ו-SKU */}
					<div className="flex items-center justify-between text-sm">
						<span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full font-medium">
							{product.category === 'necklaces' && 'שרשראות'}
							{product.category === 'earrings' && 'עגילים'}
							{product.category === 'bracelets' && 'צמידים'}
							{product.category === 'rings' && 'טבעות'}
							{!['necklaces', 'earrings', 'bracelets', 'rings'].includes(product.category) && product.category}
						</span>
						{product.sku && (
							<span className="text-gray-400 font-mono text-xs">
								{product.sku}
							</span>
						)}
					</div>

					{/* מחיר */}
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<span className="text-2xl font-bold text-gray-900">
								₪{price}
							</span>
							{hasDiscount && (
								<span className="text-lg text-gray-400 line-through">
									₪{originalPrice}
								</span>
							)}
						</div>
						{product.inStock ? (
							<span className="flex items-center gap-1 text-emerald-600 text-sm font-medium">
								<div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
								במלאי
							</span>
						) : (
							<span className="flex items-center gap-1 text-red-600 text-sm font-medium">
								<div className="w-2 h-2 bg-red-500 rounded-full"></div>
								אזל
							</span>
						)}
					</div>

					{/* כפתורי פעולה */}
					<div className="flex gap-3 pt-2">
						<motion.button
							onClick={handleAddToCart}
							disabled={!product.inStock}
							className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium transition-all duration-300 ${
								product.inStock
									? 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white shadow-lg hover:shadow-purple-500/25'
									: 'bg-gray-100 text-gray-400 cursor-not-allowed'
							}`}
							whileHover={product.inStock ? { scale: 1.02 } : {}}
							whileTap={product.inStock ? { scale: 0.98 } : {}}
						>
							<ShoppingCart className="w-5 h-5" />
							{product.inStock ? 'הוסף לסל' : 'אזל מהמלאי'}
						</motion.button>

						<motion.button
							onClick={handleViewDetails}
							className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all duration-300 font-medium"
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
						>
							<Eye className="w-5 h-5" />
						</motion.button>
					</div>

					{/* אזור מידע נוסף */}
					{(product.material || product.stone) && (
						<div className="border-t border-gray-100 pt-4 space-y-2">
							{product.material && (
								<div className="flex items-center gap-2 text-sm text-gray-600">
									<Shield className="w-4 h-4" />
									<span>{product.material}</span>
								</div>
							)}
							{product.stone && product.stone !== 'none' && (
								<div className="flex items-center gap-2 text-sm text-gray-600">
									<Sparkles className="w-4 h-4" />
									<span>{product.stone}</span>
								</div>
							)}
						</div>
					)}
				</div>
			</motion.div>

			{/* מודל תמונה */}
			<AnimatePresence>
				{isModalOpen && (
					<motion.div 
						className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						onClick={handleModalClose}
					>
						<motion.div 
							ref={modalRef}
							className="relative max-w-4xl max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl"
							initial={{ scale: 0.8, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							exit={{ scale: 0.8, opacity: 0 }}
							transition={{ type: "spring", damping: 20, stiffness: 300 }}
							onClick={(e) => e.stopPropagation()}
						>
							<button
								onClick={handleModalClose}
								className="absolute top-4 right-4 z-10 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors"
							>
								<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
							
							<img 
								src={images[currentImageIndex]} 
								alt={product.name || 'מוצר'}
								className="w-full h-full object-contain"
								style={{ maxHeight: '80vh' }}
								onLoad={() => {
									console.log('✅ Modal image loaded successfully');
								}}
								onError={(e) => {
									console.log('❌ Modal image failed to load');
									e.target.src = '/images/placeholder.svg';
								}}
							/>
							
							{/* ניווט בין תמונות במודל */}
							{images.length > 1 && (
								<div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-white/90 backdrop-blur-sm rounded-full px-6 py-3">
									<button
										onClick={(e) => {
											e.stopPropagation();
											setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
										}}
										className="p-2 hover:bg-gray-100 rounded-full transition-colors"
									>
										<ChevronLeft className="w-5 h-5" />
									</button>
									<span className="text-sm font-medium px-4">
										{currentImageIndex + 1} / {images.length}
									</span>
									<button
										onClick={(e) => {
											e.stopPropagation();
											setCurrentImageIndex((prev) => (prev + 1) % images.length);
										}}
										className="p-2 hover:bg-gray-100 rounded-full transition-colors"
									>
										<ChevronRight className="w-5 h-5" />
									</button>
								</div>
							)}
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>
		</>
	);
};

export default ProductCard;